"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ChevronDown, ChevronRight, PanelLeftOpen } from "lucide-react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import {
  buildDocsHref,
  docsProducts,
  DOCS_PRODUCT_QUERY_PARAM,
  getSidebarProductNavigation,
  NavBadgeTone,
  NavItem,
  resolveDocsProduct,
} from "@/config/navigation";
import { DocsProductTabs } from "@/components/DocsProductTabs";

const badgeStyles: Record<NavBadgeTone, string> = {
  blue: "border-[#d7e4ff] bg-[#eef4ff] text-[#335AFF] dark:border-[#335AFF]/30 dark:bg-[#335AFF]/20 dark:text-[#8da4ff]",
  green: "border-[#cfe8db] bg-[#edf8f1] text-[#18834b] dark:border-[#18834b]/30 dark:bg-[#18834b]/20 dark:text-[#4ade80]",
  amber: "border-[#f4e3bb] bg-[#fff7e7] text-[#9a6700] dark:border-[#9a6700]/30 dark:bg-[#9a6700]/20 dark:text-[#fbbf24]",
  violet: "border-[#eadcff] bg-[#f5efff] text-[#6e3fd1] dark:border-[#6e3fd1]/30 dark:bg-[#6e3fd1]/20 dark:text-[#c084fc]",
  slate: "border-[#dde3ee] bg-[#f6f8fb] text-[#516076] dark:border-[#516076]/30 dark:bg-[#516076]/20 dark:text-[#94a3b8]",
};

function containsPath(item: NavItem, pathname: string): boolean {
  if (item.href === pathname) {
    return true;
  }

  return item.items?.some((child) => containsPath(child, pathname)) ?? false;
}

function Badge({ label, tone = "blue" }: { label: string; tone?: NavBadgeTone }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-[0.12em]",
        badgeStyles[tone]
      )}
    >
      {label}
    </span>
  );
}

function NavLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const isActive = containsPath(item, pathname);
  const searchParams = useSearchParams();
  const activeProduct = resolveDocsProduct(searchParams.get(DOCS_PRODUCT_QUERY_PARAM), pathname || undefined);

  return (
    <Link
      href={item.href ? buildDocsHref(item.href, activeProduct, searchParams) : "#"}
      className={clsx(
        "group flex items-start gap-3 rounded-xl px-2.5 py-2.5 text-[13px] transition-all duration-200",
        isActive
          ? "bg-[#eef4ff]/88 text-[#1736aa] shadow-[inset_0_0_0_1px_rgba(51,90,255,0.08)] dark:bg-[#335AFF]/14 dark:text-[#8da4ff]"
          : "text-[#52607a] hover:bg-black/[0.02] hover:text-[#0f172a] dark:text-[#94a3b8] dark:hover:bg-white/[0.04] dark:hover:text-[#f8fafc]"
      )}
    >
      <span
        className={clsx(
          "flex-shrink-0 rounded-full transition-colors",
          "mt-[0.42rem] h-1.5 w-1.5",
          isActive
            ? "bg-[#335AFF] dark:bg-[#8da4ff]"
            : "bg-[#bcc8d9] group-hover:bg-[#7c8fb0] dark:bg-[#475569] dark:group-hover:bg-[#94a3b8]"
        )}
      />
      <span className={clsx("min-w-0 flex-1 leading-5", isActive && "font-semibold")}>{item.title}</span>
      {item.badge ? <Badge label={item.badge} tone={item.badgeTone} /> : null}
    </Link>
  );
}

function NavSubGroup({ item, pathname, depth = 1 }: { item: NavItem; pathname: string; depth?: number }) {
  const hasActiveChild = containsPath(item, pathname);
  const [isOpen, setIsOpen] = useState(hasActiveChild);
  const isDeep = depth >= 2;

  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  if (!item.items) {
    return null;
  }

  return (
    <div className={clsx(isDeep ? "mb-2" : "mb-3")}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          "flex w-full items-center justify-between gap-3 rounded-xl text-left transition-all duration-200",
          isDeep ? "px-2 py-1.5" : "px-2.5 py-2.5",
          hasActiveChild || isOpen
            ? "text-[#0f172a] dark:text-[#f8fafc]"
            : "text-[#61708a] hover:bg-black/[0.02] hover:text-[#0f172a] dark:text-[#94a3b8] dark:hover:bg-white/[0.04] dark:hover:text-[#f8fafc]"
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={clsx(
              "h-1.5 w-1.5 flex-shrink-0 rounded-full",
              hasActiveChild || isOpen ? "bg-[#7c8fb0] dark:bg-[#94a3b8]" : "bg-[#cfd8e6] dark:bg-[#475569]"
            )}
          />
          <span
            className={clsx(
              "truncate text-current",
              isDeep ? "text-[12px] font-medium" : "text-[13px] font-semibold"
            )}
          >
            {item.title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {item.badge ? <Badge label={item.badge} tone={item.badgeTone} /> : null}
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-[#94a3b8] dark:text-[#64748b]" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[#94a3b8] dark:text-[#64748b]" />
          )}
        </div>
      </button>

      {isOpen ? (
        <div
          className={clsx(
            "space-y-0.5 border-l dark:border-[#334155]",
            isDeep
              ? "ml-[12px] mt-1 border-[#e5ebf6] pl-[10px]"
              : "ml-[14px] mt-1.5 border-[#d9e3f3] pl-[12px]"
          )}
        >
          {item.items.map((subChild) => (
            <NavTreeNode key={subChild.href || subChild.title} item={subChild} pathname={pathname} depth={depth + 1} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NavTreeNode({ item, pathname, depth = 1 }: { item: NavItem; pathname: string; depth?: number }) {
  if (item.items?.length) {
    return <NavSubGroup item={item} pathname={pathname} depth={depth} />;
  }

  return <NavLink item={item} pathname={pathname} />;
}

function NavSection({
  title,
  items,
  pathname,
}: {
  title?: string;
  items: NavItem[];
  pathname: string;
}) {
  return (
    <section className="mt-5 first:mt-0">
      {title ? (
        <div className="mb-3 px-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8a96ab] dark:text-[#64748b]">{title}</p>
        </div>
      ) : null}
      <div className="space-y-1">
        {items.map((item) => (
          <NavTreeNode key={item.href || item.title} item={item} pathname={pathname} />
        ))}
      </div>
    </section>
  );
}

export function Sidebar({ className }: { className?: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeProduct = resolveDocsProduct(searchParams.get(DOCS_PRODUCT_QUERY_PARAM), pathname || undefined);
  const productItems = getSidebarProductNavigation(activeProduct);

  return (
    <aside
      className={clsx(
        "fixed top-20 z-30 hidden h-[calc(100vh-6rem)] shrink-0 overflow-hidden py-4 pr-4 lg:sticky lg:block lg:w-full",
        className
      )}
    >
      <div className="flex h-full flex-col overflow-hidden py-4">
        <div className="border-b border-[#e2e8f0] px-3.5 pb-4 dark:border-white/10">
          <div className="flex items-center gap-3 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[1rem] bg-[#eff3ff] text-[#335AFF] dark:bg-[#335AFF]/20 dark:text-[#8da4ff]">
              <PanelLeftOpen className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#94a3b8] dark:text-[#64748b]">Docs Map</p>
              <p className="mt-0.5 text-sm font-semibold text-[#0f172a] dark:text-[#f8fafc]">文档目录</p>
            </div>
          </div>

          <DocsProductTabs />
        </div>

        <nav className="hide-scrollbar min-h-0 flex-1 overflow-y-auto pr-1 pt-4">
          <NavSection items={productItems} pathname={pathname || ""} />
        </nav>
      </div>
    </aside>
  );
}
